import React, { useState, useEffect } from 'react';
import { Switch } from '@headlessui/react';
import { useSettings } from '../hooks/useSettings';
import { TIMEZONE_OPTIONS, getTimezoneDisplayName, formatDateTime } from '../utils/timezone';
import TimezoneSelector from '../components/ui/TimezoneSelector';
import SettingsTest from '../components/SettingsTest';
import { getSyncStatus, triggerSync, SyncStatus, SyncResponse } from '../services/api';

const SettingsPage: React.FC = () => {
  const { settings, updateSetting } = useSettings();
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  
  // 同步相关状态
  const [syncStatus, setSyncStatus] = useState<SyncStatus | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState('');
  const [isLoadingStatus, setIsLoadingStatus] = useState(false);

  // 加载同步状态
  const loadSyncStatus = async () => {
    setIsLoadingStatus(true);
    try {
      const status = await getSyncStatus();
      setSyncStatus(status);
    } catch (error) {
      console.error('获取同步状态失败:', error);
      setSyncMessage('获取同步状态失败');
      setTimeout(() => setSyncMessage(''), 3000);
    } finally {
      setIsLoadingStatus(false);
    }
  };

  // 触发手动同步
  const handleManualSync = async () => {
    setIsSyncing(true);
    setSyncMessage('');
    try {
      const response = await triggerSync();
      setSyncMessage(response.message);
      
      // 同步完成后重新加载状态
      setTimeout(() => {
        loadSyncStatus();
      }, 1000);
      
      setTimeout(() => setSyncMessage(''), 5000);
    } catch (error) {
      console.error('手动同步失败:', error);
      setSyncMessage('同步失败，请稍后重试');
      setTimeout(() => setSyncMessage(''), 3000);
    } finally {
      setIsSyncing(false);
    }
  };

  // 组件挂载时加载同步状态
  useEffect(() => {
    loadSyncStatus();
  }, []);

  // 处理自动标记已读设置变更
  const handleAutoMarkReadChange = async (enabled: boolean) => {
    setIsSaving(true);
    try {
      updateSetting('autoMarkReadOnOpen', enabled);
      setSaveMessage('设置已保存');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error) {
      console.error('保存设置失败:', error);
      setSaveMessage('保存失败');
      setTimeout(() => setSaveMessage(''), 3000);
    } finally {
      setIsSaving(false);
    }
  };

  // 处理时区设置变更
  const handleTimezoneChange = async (timezone: string) => {
    setIsSaving(true);
    try {
      updateSetting('timezone', timezone);
      setSaveMessage('时区设置已保存');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error) {
      console.error('保存时区设置失败:', error);
      setSaveMessage('保存失败');
      setTimeout(() => setSaveMessage(''), 3000);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* 固定头部 */}
      <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">设置</h1>
          <p className="text-gray-600">管理您的邮件客户端偏好设置</p>
        </div>
      </div>

      {/* 可滚动内容区域 */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-6 max-w-4xl mx-auto space-y-6">
          {/* 保存状态提示 */}
          {saveMessage && (
            <div className={`p-4 rounded-lg ${
              saveMessage.includes('失败') 
                ? 'bg-red-50 text-red-700 border border-red-200' 
                : 'bg-green-50 text-green-700 border border-green-200'
            }`}>
              {saveMessage}
            </div>
          )}

          {/* 邮件设置 */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">邮件设置</h2>
              <p className="text-sm text-gray-600 mt-1">配置邮件阅读和处理行为</p>
            </div>
            
            <div className="px-6 py-4">
              {/* 自动标记已读设置 */}
              <div className="flex items-center justify-between py-4">
                <div className="flex-1">
                  <h3 className="text-base font-medium text-gray-900">
                    打开邮件详情时自动标记为已读
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    当您打开邮件详情页面时，自动将邮件状态改为已读。关闭此选项需要手动标记邮件为已读。
                  </p>
                </div>
                <div className="ml-6 flex-shrink-0">
                  <Switch
                    checked={settings.autoMarkReadOnOpen}
                    onChange={handleAutoMarkReadChange}
                    disabled={isSaving}
                    className={`${
                      settings.autoMarkReadOnOpen ? 'bg-blue-600' : 'bg-gray-200'
                    } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50`}
                  >
                    <span className="sr-only">自动标记已读</span>
                    <span
                      className={`${
                        settings.autoMarkReadOnOpen ? 'translate-x-6' : 'translate-x-1'
                      } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                    />
                  </Switch>
                </div>
              </div>
            </div>
          </div>

          {/* 通知设置 */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">通知设置</h2>
              <p className="text-sm text-gray-600 mt-1">配置邮件通知和提醒</p>
            </div>
            
            <div className="px-6 py-4">
              <div className="text-center py-8 text-gray-500">
                <p>更多通知设置即将推出...</p>
              </div>
            </div>
          </div>

          {/* 界面设置 */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">界面设置</h2>
              <p className="text-sm text-gray-600 mt-1">自定义界面外观和布局</p>
            </div>
            
            <div className="px-6 py-4 space-y-6">
              {/* 时区设置 */}
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="text-base font-medium text-gray-900">
                    时区设置
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    选择您所在的时区，邮件时间将按此时区显示
                  </p>
                  <div className="mt-2 text-xs text-gray-500">
                    当前时间预览: {formatDateTime(new Date(), { showTimezone: true })}
                  </div>
                </div>
                <div className="ml-6 flex-shrink-0 w-80">
                  <TimezoneSelector
                    value={settings.timezone}
                    onChange={(timezone) => handleTimezoneChange(timezone)}
                    disabled={isSaving}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* 高级设置 */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">高级设置</h2>
              <p className="text-sm text-gray-600 mt-1">高级功能和实验性选项</p>
            </div>
            
            <div className="px-6 py-4">
              <div className="text-center py-8 text-gray-500">
                <p>高级设置选项即将推出...</p>
              </div>
            </div>
          </div>

          {/* 数据管理 */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">数据管理</h2>
              <p className="text-sm text-gray-600 mt-1">管理您的数据同步和隐私设置</p>
            </div>
            
            <div className="px-6 py-4 space-y-6">
              {/* 数据同步状态 */}
              <div className="border-b border-gray-200 pb-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-base font-medium text-gray-900">数据同步状态</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      检查发件人规则和聊天室数据的同步状态
                    </p>
                  </div>
                  <button
                    onClick={loadSyncStatus}
                    disabled={isLoadingStatus}
                    className="ml-4 px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 disabled:opacity-50"
                  >
                    {isLoadingStatus ? '检查中...' : '刷新状态'}
                  </button>
                </div>
                
                {syncStatus && (
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">总发件人数量:</span>
                        <span className="font-medium">{syncStatus.total_senders}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">发件人规则数量:</span>
                        <span className="font-medium">{syncStatus.total_rules}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">规则同步状态:</span>
                        <span className={`font-medium ${syncStatus.rules_synced ? 'text-green-600' : 'text-red-600'}`}>
                          {syncStatus.rules_synced ? '✅ 已同步' : '❌ 需要同步'}
                        </span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">用户邮件发件人:</span>
                        <span className="font-medium">{syncStatus.user_senders}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">聊天室数量:</span>
                        <span className="font-medium">{syncStatus.total_chat_rooms}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">聊天室同步状态:</span>
                        <span className={`font-medium ${syncStatus.chat_rooms_synced ? 'text-green-600' : 'text-red-600'}`}>
                          {syncStatus.chat_rooms_synced ? '✅ 已同步' : '❌ 需要同步'}
                        </span>
                      </div>
                    </div>
                    <div className="col-span-2 pt-2 border-t border-gray-100">
                      <div className="flex justify-between">
                        <span className="text-gray-600">回复邮件数量:</span>
                        <span className="font-medium">{syncStatus.total_replies}</span>
                      </div>
                    </div>
                  </div>
                )}
                
                {!syncStatus && !isLoadingStatus && (
                  <div className="text-center py-4 text-gray-500">
                    <p>点击"刷新状态"获取同步信息</p>
                  </div>
                )}
              </div>
              
              {/* 手动同步 */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-base font-medium text-gray-900">手动同步</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      手动触发数据同步，确保发件人规则和聊天室数据保持一致
                    </p>
                  </div>
                  <button
                    onClick={handleManualSync}
                    disabled={isSyncing || (syncStatus?.rules_synced && syncStatus?.chat_rooms_synced)}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                      isSyncing 
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : syncStatus?.rules_synced && syncStatus?.chat_rooms_synced
                        ? 'bg-green-100 text-green-700 cursor-not-allowed'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    {isSyncing ? '同步中...' : 
                     syncStatus?.rules_synced && syncStatus?.chat_rooms_synced ? '已同步' : '开始同步'}
                  </button>
                </div>
                
                {syncMessage && (
                  <div className={`p-3 rounded-lg text-sm ${
                    syncMessage.includes('失败') || syncMessage.includes('错误')
                      ? 'bg-red-50 text-red-700 border border-red-200'
                      : 'bg-green-50 text-green-700 border border-green-200'
                  }`}>
                    {syncMessage}
                  </div>
                )}
                
                {syncStatus && (!syncStatus.rules_synced || !syncStatus.chat_rooms_synced) && (
                  <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-yellow-700">
                          检测到数据不同步，建议执行手动同步以确保数据一致性。
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* 设置说明 */}
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800">关于设置</h3>
                <div className="mt-2 text-sm text-blue-700">
                  <p>
                    所有设置都保存在您的浏览器本地存储中，不会上传到服务器。
                    如果您清除浏览器数据，这些设置将会重置为默认值。
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* 开发环境下显示测试组件 */}
          {process.env.NODE_ENV === 'development' && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h3 className="text-sm font-medium text-yellow-800 mb-2">开发调试</h3>
              <SettingsTest />
            </div>
          )}

          {/* 底部间距 */}
          <div className="h-8"></div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage; 